import { measure } from '@/components/Analytics/Analytics';
import CachedCardMediaContext, {
	maxCacheSize,
} from '@/context/CachedCardMediaContext';
import useSpecies from '@/hooks/species/useSpecies';
import useKeyValueStorage from '@/hooks/storage/useKeyValueStorage';
import useStorage from '@/hooks/storage/useStorage';
import usePhysicalLocation from '@/hooks/usePhysicalLocation';
import { postToAPI } from '@/lib/api';
import { getMimeTypeFromDataUrl } from '@/lib/file';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
	Card,
	CardMedia,
	CardMediaType,
	getAllCardMediaWithTypes,
} from './useCards';

const useCardsOnlyForContext = () => {
	// Extra data needed for analytics
	const { province } = usePhysicalLocation();
	const { findSpeciesById } = useSpecies();

	const { value: cardsUnsorted, setValue: setCards } = useKeyValueStorage<Card[]>(
		'cards',
		[],
	);

	// Sort them by media dates, latest first
	const cards = useMemo(() => {
		const sorted = [...cardsUnsorted]
			// Put the latest cards first
			.sort((a, b) => {
				const aLatest = getAllCardMediaWithTypes(a)
					.map((m) => new Date(m.timestamp).getTime())
					.sort((x, y) => y - x)[0];

				const bLatest = getAllCardMediaWithTypes(b)
					.map((m) => new Date(m.timestamp).getTime())
					.sort((x, y) => y - x)[0];

				return bLatest - aLatest;
			})
			// Also sort the media of each card by timestamp to put the latest media first
			.map((card) => {
				card.photos.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
				card.audio?.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
				return card;
			});
		return sorted;
	}, [cardsUnsorted]);

	// Remember a few loaded photos in memory to avoid reloading them every time
	const cachedCardMedia = useContext(CachedCardMediaContext);
	const {
		saveFileBase64,
		readFileBase64,
		readImageThumbnailBase64,
		deleteFile,
	} = useStorage();

	// Cards as super fast map indexed by species id
	const cardsBySpeciesId = useMemo(
		() =>
			cards.reduce((map, card) => {
				map.set(card.speciesId, card);
				return map;
			}, new Map<number, Card>()),
		[cards],
	);

	useEffect(() => {
		// Restore 'identifiedBy' field for old cards (set to 'automatically')
		// Check if there are any cards without the 'identifiedBy' field
		if (
			cards.some((card) =>
				card.photos.some(
					(photo) => typeof photo.identifiedBy !== 'string',
				),
			)
		) {
			// Update cards to include the 'identifiedBy' field
			const newCards = cards.map((card) => ({
				...card,
				photos: card.photos.map((photo) => ({
					...photo,
					identifiedBy: photo.identifiedBy ?? 'automatically',
				})),
			}));
			setCards(newCards);
		}
		// Create audio field for old cards
		if (cards.some((card) => !card.audio)) {
			const newCards = cards.map((card) => ({
				...card,
				audio: [],
			}));
			setCards(newCards);
		}
		// Cards that don't have a mime yet can be restored by adding the jpg and wav mime types
		if (
			cards.some((card) =>
				getAllCardMediaWithTypes(card).some(
					(cardMedia) => !cardMedia.mime,
				),
			)
		) {
			const newCards = cards.map((card) => ({
				...card,
				photos: card.photos.map((photo) => ({
					...photo,
					mime: photo.mime ?? 'image/jpg',
				})),
				audio: (card.audio ?? []).map((audio) => ({
					...audio,
					mime: audio.mime ?? 'audio/wav',
				})),
			}));
			setCards(newCards);
		}
	}, [cards, setCards]);

	// Get card by species id if it exists
	const getCardBySpeciesId = useCallback(
		(speciesId: number) => cardsBySpeciesId.get(speciesId) ?? null,
		[cardsBySpeciesId],
	);

	// Get media item extension
	const getMediaItemExtension = useCallback(
		(type: CardMediaType) => (type === 'photo' ? 'jpg' : 'wav'),
		[],
	);

	// Get media item mime type
	const getMediaItemMimeType = useCallback(
		(type: CardMediaType) =>
			type === 'photo' ? 'image/jpeg' : 'audio/wav',
		[],
	);

	// Get media item path
	const getMediaItemPath = useCallback(
		(id: string, type: CardMediaType) =>
			`cards/${id}.${getMediaItemExtension(type)}`,
		[getMediaItemExtension],
	);

	// Store media in (new) card
	const storeMedia = useCallback(
		async (
			speciesId: number,
			type: CardMediaType,
			fileBase64: string,
			identifiedAs: string,
			identifiedBy: CardMedia['identifiedBy'],
			restoreValues?: {
				id: string;
				timestamp: string;
			},
		) => {
			const species = findSpeciesById(speciesId);
			if (!species) return;

			// Pre-check for duplicates based on current card (best-effort)
			if (restoreValues) {
				const existingCard = getCardBySpeciesId(speciesId);
				if (
					existingCard &&
					getAllCardMediaWithTypes(existingCard).some(
						(media) =>
							media.id === restoreValues.id,
					)
				) {
					return;
				}
			}

			const timeStampDate = new Date();
			timeStampDate.setMilliseconds(0);
			const timeStampString = restoreValues?.timestamp ?? timeStampDate.toISOString();

			// Store the file first
			const mediaId = restoreValues?.id ?? uuidv4();
			await saveFileBase64(getMediaItemPath(mediaId, type), fileBase64);
			const storedMedia: CardMedia = {
				id: mediaId,
				timestamp: timeStampString,
				identifiedAs: identifiedAs ?? species.attributes.titleLong,
				identifiedBy,
				mime: getMimeTypeFromDataUrl(fileBase64),
			};

			// Update state atomically using functional update to avoid stale closures
			setCards((prev) => {
				// Check duplicates against latest prev
				const prevCardIndex = prev.findIndex((c) => c.speciesId === speciesId);
				const prevCard = prevCardIndex === -1 ? null : prev[prevCardIndex];
				if (
					prevCard &&
					getAllCardMediaWithTypes(prevCard).some(
						(m) => m.id === storedMedia.id || m.timestamp === storedMedia.timestamp,
					)
				) {
					return prev; // skip duplicate
				}

				if (!prevCard) {
					return [
						...prev,
						{
							id: uuidv4(),
							speciesId,
							photos: type === 'photo' ? [storedMedia] : [],
							audio: type === 'audio' ? [storedMedia] : [],
						},
					];
				}

				const next = [...prev];
				const nextCard = { ...prevCard };
				if (type === 'photo') {
					nextCard.photos = [storedMedia, ...prevCard.photos];
				}
				if (type === 'audio') {
					const aud = prevCard.audio ?? [];
					nextCard.audio = [storedMedia, ...aud];
				}
				next[prevCardIndex] = nextCard;
				return next;
			});

			// Measure analytics per province
			measure(
				`card-stored-${speciesId}-${species.attributes.slug}-${province}-${type}`,
			);
		},
		[
			findSpeciesById,
			getCardBySpeciesId,
			saveFileBase64,
			getMediaItemPath,
			province,
			setCards,
		],
	);

	// Identify and store if correct
	const identifyAndStore = useCallback(
		async (
			fileBase64: string,
			type: CardMediaType,
			targetSpeciesId: string | number,
			allowOrangeLight: boolean = false,
			additionalMediaBase64: string[] = [],
		): Promise<
			| {
				is_stored?: boolean;
				light: 'GREEN' | 'ORANGE' | 'RED';
				species_id: number;
				identified_as: string;
				algorithm_step: string;
			}
			| undefined
		> =>
			await fetch(fileBase64)
				.then((res) => res.blob())
				.then(async (blob) => {
					// Add base information
					const getFileExtensionFromBase64 = (base64: string) =>
						base64.split(';')[0].split('/')[1];
					const formData = new FormData();
					formData.append('species_id', targetSpeciesId.toString());
					if (type === 'photo')
						formData.append(
							'image',
							blob,
							`image.${getFileExtensionFromBase64(fileBase64)}`,
						);
					if (type === 'audio')
						formData.append(
							'audio',
							blob,
							`audio.${getFileExtensionFromBase64(fileBase64)}`,
						);

					// Add additional media, if it's needed
					(
						await Promise.all(
							additionalMediaBase64.map((base64) =>
								fetch(base64).then((res) => res.blob()),
							),
						)
					).forEach((blob, index) => {
						formData.append(
							`additional_media[${index}]`,
							blob,
							`additional_media_${index}.${getFileExtensionFromBase64(
								additionalMediaBase64[index],
							)}`,
						);
					});

					// Run the identification
					return await postToAPI(`/identify/${type}`, formData).then(
						async (data) => {
							// If the light is green, store the photo and navigate back
							const shouldStore =
								data.light === 'GREEN' ||
								(allowOrangeLight && data.light === 'ORANGE');
							if (shouldStore) {
								await storeMedia(
									data.species_id,
									type,
									fileBase64,
									data.identified_as,
									'automatically',
								);
							}
							return {
								is_stored: shouldStore,
								...data,
							};
						},
					);
				})
				.catch((error) => {
					console.error('Error:', error);
					// Notify of error
					alert(
						'Ben je verbonden met het internet? Het checken is niet gelukt.',
					);
					return undefined;
				}),
		[storeMedia],
	);

	// Read photo from card
	const readPhoto = useCallback(
		async (photoId: string, asThumbnail: boolean = false) => {
			const cacheKey = asThumbnail ? 'thumbnails' : 'full';

			// Check if the photo is already in memory
			if (cachedCardMedia[cacheKey].has(photoId)) {
				return cachedCardMedia[cacheKey].get(photoId);
			}
			const photoDataUrl = asThumbnail
				? await readImageThumbnailBase64(
					getMediaItemPath(photoId, 'photo'),
					320,
					320,
				)
				: await readFileBase64(getMediaItemPath(photoId, 'photo'));
			if (!photoDataUrl) return null;

			// Store the photo in memory
			cachedCardMedia[cacheKey].set(photoId, photoDataUrl);

			// If memory is full, remove the oldest photo
			if (cachedCardMedia[cacheKey].size > maxCacheSize[cacheKey]) {
				const oldestPhotoId = cachedCardMedia[cacheKey]
					.keys()
					.next().value;
				if (oldestPhotoId) cachedCardMedia[cacheKey].delete(oldestPhotoId);
			}
			return photoDataUrl;
		},
		[
			cachedCardMedia,
			readImageThumbnailBase64,
			getMediaItemPath,
			readFileBase64,
		],
	);

	// Read audio from card
	const readAudio = useCallback(
		async (audioId: string) => {
			// Check if the audio is already in memory
			if (cachedCardMedia['full'].has(audioId)) {
				return cachedCardMedia['full'].get(audioId);
			}
			const audioDataUrl = await readFileBase64(
				getMediaItemPath(audioId, 'audio'),
			);
			if (!audioDataUrl) return null;

			// Store the audio in memory
			cachedCardMedia['full'].set(audioId, audioDataUrl);

			// If memory is full, remove the oldest audio
			if (cachedCardMedia['full'].size > maxCacheSize['full']) {
				const oldestAudioId = cachedCardMedia['full']
					.keys()
					.next().value;
				if (oldestAudioId) cachedCardMedia['full'].delete(oldestAudioId);
			}
			return audioDataUrl;
		},
		[cachedCardMedia, readFileBase64, getMediaItemPath],
	);

	// Delete media item from card
	const deleteMediaItem = useCallback(
		async (mediaId: string) => {
			// Remove the photo or audio from the card
			const newCards = cards.map((card) => ({
				...card,
				photos: card.photos.filter((photo) => photo.id !== mediaId),
				audio: card.audio?.filter((audio) => audio.id !== mediaId),
			}));
			// Also filter out cards with 0 photos and 0 audio
			setCards(
				newCards.filter(
					(card) =>
						card.photos.length + (card.audio?.length ?? 0) > 0,
				),
			);

			// Remove the photo from memory
			cachedCardMedia['full'].delete(mediaId);
			cachedCardMedia['thumbnails'].delete(mediaId);
			// Delete the photo from disk
			await deleteFile(getMediaItemPath(mediaId, 'photo'));
		},
		[cards, setCards, cachedCardMedia, deleteFile, getMediaItemPath],
	);

	return {
		cards,
		getCardBySpeciesId,
		identifyAndStore,
		storeMedia,
		getMediaItemExtension,
		getMediaItemMimeType,
		getMediaItemPath,
		readPhoto,
		readAudio,
		deleteMediaItem,
	};
};

export default useCardsOnlyForContext;
